import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/shared/components/ui/Button';
import { Tabs } from '@/shared/components/ui/Tabs';
import type { TabItem } from '@/shared/components/ui/Tabs';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { useCurrentUser } from '../hooks';
import { AccountTab } from '../components/AccountTab';
import { NotificationsTab } from '../components/NotificationsTab';
import { SecurityTab } from '../components/SecurityTab';
import { McpTab } from '../components/mcp/McpTab';
import { AccountSaveContext, type AccountSaveMeta } from '../components/account-save-context';

type TabType = 'account' | 'notifications' | 'security' | 'mcp';

const TAB_META: { id: TabType; label: string; icon: IconName }[] = [
  { id: 'account', label: 'Minha Conta', icon: 'user' },
  { id: 'notifications', label: 'Notificações', icon: 'bell' },
  { id: 'security', label: 'Segurança', icon: 'lock' },
  { id: 'mcp', label: 'Conexões MCP', icon: 'link' },
];

/**
 * Account Page (Configurações)
 * User profile management and security settings — full-page FORM (never modal).
 *
 * DS form contract: sticky header with breadcrumb (never a back arrow) + title +
 * a SINGLE primary Save button. The page is multi-tab (Conta, Notificações,
 * Segurança, Conexões MCP); each tab publishes its own save action to the header
 * via AccountSaveContext. Tabs seguem o TEMA da página (não claro-fixo).
 */
export const AccountPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: user, isLoading, isError } = useCurrentUser();

  // Header save-button coordination (single primary button, driven by active tab)
  const onSaveRef = useRef<(() => void | Promise<void>) | null>(null);
  const [saveMeta, setSaveMeta] = useState<AccountSaveMeta>({
    visible: false,
    saving: false,
    disabled: false,
    label: 'Salvar alterações',
    loadingLabel: 'Salvando...',
  });
  // Stable context identity — required so tab-side effects don't re-run every render.
  const saveContextValue = useMemo(() => ({ onSaveRef, setSaveMeta }), []);

  // Determine active tab from URL
  const getTabFromPath = (): TabType => {
    const path = location.pathname;
    if (path.includes('/notifications')) return 'notifications';
    if (path.includes('/security')) return 'security';
    if (path.includes('/mcp')) return 'mcp';
    return 'account';
  };

  const activeTab = getTabFromPath();
  const activeLabel = TAB_META.find((t) => t.id === activeTab)?.label ?? 'Minha Conta';

  // Redirect /settings to /settings/account
  useEffect(() => {
    if (location.pathname === '/settings') {
      navigate('/settings/account', { replace: true });
    }
  }, [location.pathname, navigate]);

  const tabItems: TabItem[] = [
    { id: 'account', label: 'Conta', icon: 'user', content: <AccountTab /> },
    { id: 'notifications', label: 'Notificações', icon: 'bell', content: <NotificationsTab /> },
    { id: 'security', label: 'Segurança', icon: 'lock', content: <SecurityTab /> },
    { id: 'mcp', label: 'Conexões (MCP)', icon: 'link', content: <McpTab /> },
  ];

  const handleHeaderSave = (): void => {
    void onSaveRef.current?.();
  };

  // Loading State
  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
        data-testid="account-loading"
      >
        <div className="flex items-center gap-3">
          <Icon name="loader" className="w-6 h-6 animate-spin" style={{ color: 'var(--theme-primary)' }} />
          <p className="text-lg" style={{ color: 'var(--theme-card-text)' }}>Carregando dados...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (isError || !user) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
        data-testid="account-error"
      >
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <Icon name="alert-circle" className="w-12 h-12" style={{ color: 'var(--theme-error-color)' }} />
          <h2 className="text-xl font-semibold" style={{ color: 'var(--theme-card-text)' }}>Erro ao carregar dados</h2>
          <p style={{ color: 'var(--theme-muted-text)' }}>
            Não foi possível carregar suas informações. Tente novamente mais tarde.
          </p>
          <Button onClick={() => navigate('/')}>
            Voltar ao Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <AccountSaveContext.Provider value={saveContextValue}>
      <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        {/* Sticky Header: breadcrumb (nunca seta-voltar) + título + Salvar (único primário) */}
        <div
          className="flex-shrink-0"
          style={{
            backgroundColor: 'var(--theme-header-background)',
            borderBottom: '1px solid var(--theme-header-border)',
          }}
        >
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-3">
            <div className="flex items-center justify-between gap-3">
              <nav className="flex items-center gap-2 min-w-0" aria-label="breadcrumb">
                <button
                  type="button"
                  onClick={() => navigate('/settings/account')}
                  className="text-sm font-medium transition-colors hover:underline flex-shrink-0"
                  style={{ color: 'var(--theme-muted-text)' }}
                  data-testid="account-breadcrumb-root"
                >
                  Configurações
                </button>
                <Icon
                  name="chevron-right"
                  className="w-4 h-4 flex-shrink-0"
                  style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }}
                />
                <span
                  className="text-lg font-semibold tracking-tight truncate"
                  style={{ color: 'var(--theme-header-text)' }}
                  data-testid="account-breadcrumb-current"
                >
                  {activeLabel}
                </span>
              </nav>

              {saveMeta.visible && (
                <Button
                  onClick={handleHeaderSave}
                  disabled={saveMeta.disabled || saveMeta.saving}
                  loading={saveMeta.saving}
                  icon="check"
                  className="flex-shrink-0"
                  style={{
                    backgroundColor: 'var(--theme-button-primary)',
                    color: 'var(--theme-button-primary-text)',
                  }}
                  data-testid="account-save-button"
                >
                  {saveMeta.saving ? saveMeta.loadingLabel : saveMeta.label}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs + Content */}
        <Tabs
          tabs={tabItems}
          activeTab={activeTab}
          onChange={(tabId) => navigate(`/settings/${tabId}`)}
          variant="underline"
          pageLevel
        />
      </div>
    </AccountSaveContext.Provider>
  );
};
